import { TypesErrors } from '@app/enums/errors.enum';
import { ContentHeaderErrorProps } from '@app/interfaces';
import { buildSsbd503PrintLines } from '@app/utils/ssbd-http-503-print.util';

const format =
  '' +
  /*start headers*/
  'PT^' +
  '^?B9Z' +
  '^@@' +
  '^C?ATK' +
  '^C?ACI' +
  '^C?ABD' +
  '^0101110112011301210122012301310132013301410142014301' +
  '^02011160216031604160' +
  /*end headers*/

  '^0396C02C' +
  '^0496E02C' +
  '^0596I02C' +
  '^0696J02C' +
  '^0796L02C' +
  '^0896M02C' +
  //C F H J L N O P Q R

  '^0906O02L' +
  '^1006O11L' +
  '^1106O16L' +
  '^1210O22L' +
  '^1307O34L' +
  '^1414O43L' +
  '^1516O56L' +
  '^1606P02L' +
  '^1706P11L' +
  '^1806P16L' +
  '^1910P22L' +
  '^2011P34L' +
  '^2111P43L' +
  '^2216P56L' +
  '^23B6H321231^';

function prepPectabDynamicContent(id, error, pnr, reservation, passenger, langLocal, msg = '') {
  const currentTime = new Date();
  let data: any = {
    code: '',
    messages: {},
    pnr: '',
    from: '',
    to: '',
    departure: '',
    flight: '',
    currentTime:
      currentTime.getDate() +
      '/' +
      (currentTime.getMonth() + 1) +
      '/' +
      currentTime.getFullYear() +
      ' ' +
      currentTime.getHours() +
      ':' +
      currentTime.getMinutes(),
    passenger: '',
  };

  if (langLocal !== 'es_MX') {
    langLocal = 'en_US';
  }

  data.messages = getErrorCardMessage(id, langLocal, msg);

  data.code = error?.code || '';
  if (id !== TypesErrors.SSBD_HTTP_503) {
    data.messages.messageLong = error?.msg?.replace(',', '') ?? '';
  } else if (error?.failedRequest) {
    const dynamicLines = buildSsbd503PrintLines(error.failedRequest, langLocal);
    data.messages.header4 = dynamicLines.header4;
    data.messages.messageLong = dynamicLines.messageLong;
  }
  data.pnr = pnr || '';
  data.from = reservation?.departureAirport || '';
  data.to = reservation?.arrivalAirport || '';
  data.departure = reservation?.estimatedDepartureDateTime || '';
  data.flight = reservation?.number || '';
  data.passenger += (passenger && passenger[0]?.id) || '';
  data.passenger += (data.passenger && ': ') || '';
  data.passenger += (passenger && passenger[0]?.displayName) || '';

  const content =
    '' +
    /*start headers*/
    'CP^A^01B^CP^01W^021' +
    /*end headers*/
    '^03 ' +
    data.messages.header1 +
    '^04 ' +
    data.messages.header2 +
    '^05 ' +
    data.messages.header3 +
    '^06 ' +
    data.messages.header4 +
    '^07' +
    data.messages.messageLong +
    '^08' +
    data.messages.code +
    '^09PNR:  ' +
    '^10FROM: ' +
    '^11TO:   ' +
    '^12DEPARTURE:' +
    '^13FLIGHT:' +
    '^14CURRENT TIME:' +
    '^15PASSENGER:' +
    //    start footer data
    '^16' +
    data.pnr +
    '^17' +
    data.from +
    '^18' +
    data.to +
    '^19' +
    data.departure +
    '^20' +
    data.flight +
    '^21' +
    data.currentTime +
    '^22' +
    data.passenger +
    '^23' +
    data.pnr +
    '^';

  return content;
}

function getErrorCardMessage(id: number, lang: string, msg: string): ContentHeaderErrorProps {
  const defaultKey = 'default';
  const contentsErrors = {
    [TypesErrors.UNACCOMPANIED_MINOR]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: '',
        code: 'UMNR',
        messageLong: 'UNACCOMPANIED_MINOR',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: 'Please hand this card to an agent to receive attention at the counter',
        header4: '',
        code: 'UMNR',
        messageLong: 'UNACCOMPANIED_MINOR',
      },
    },
    [TypesErrors.LOAD_BALANCER]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: '',
        code: 'LOAD_BALANCER',
        messageLong: 'Please try again later.',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: 'Please hand this card to an agent to receive attention at the counter',
        header4: '',
        code: 'LOAD_BALANCER',
        messageLong: 'Please try again later.',
      },
    },
    [TypesErrors.PNR_LOOKUP_FAILURE]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '                Tu reservación no fue encontrada (PNR)               ',
        header4: '    Para continuar con tu proceso dirigete a nuestros mostradores     ',
        code: 'PNR_LOOKUP_FAILURE',
        messageLong: 'Failed to find the pnr.',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: '                Your reservation was not found (PNR)                 ',
        header4: '       To continue with your process proceed to our counters         ',
        code: 'PNR_LOOKUP_FAILURE',
        messageLong: 'Failed to find the pnr.',
      },
    },
    [TypesErrors.PP_SCAN_FAIL]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: '',
        code: 'PP_SCAN_FAIL',
        messageLong: 'Reservation not found by scanning passport.',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: 'Please hand this card to an agent to receive attention at the counter',
        header4: '',
        code: 'PP_SCAN_FAIL',
        messageLong: 'Reservation not found by scanning passport.',
      },
    },
    [TypesErrors.DEST_SEARCH_FAIL]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '                Tu reservación no fue encontrada (PNR)               ',
        header4: '    Para continuar con tu proceso dirigete a nuestros mostradores     ',
        code: 'DEST_SEARCH_FAIL',
        messageLong: 'Failed to find reservation by destination.',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: '                Your reservation was not found (PNR)                 ',
        header4: '       To continue with your process proceed to our counters         ',
        code: 'DEST_SEARCH_FAIL',
        messageLong: 'Failed to find reservation by destination.',
      },
    },
    [TypesErrors.FLIGHT_SEARCH_FAIL]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '                Tu reservación no fue encontrada (PNR)               ',
        header4: '    Para continuar con tu proceso dirigete a nuestros mostradores     ',
        code: 'FLIGHT_SEARCH_FAIL',
        messageLong: 'Failed to find reservation by flight number.',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: '                Your reservation was not found (PNR)                 ',
        header4: '       To continue with your process proceed to our counters         ',
        code: 'FLIGHT_SEARCH_FAIL',
        messageLong: 'Failed to find reservation by flight number.',
      },
    },
    [TypesErrors.PAID_SEAT_AT_COUNTER]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: '',
        code: 'PAID_SEAT_AT_COUNTER',
        messageLong: 'Opted to pay for seat at counter.',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: '',
        code: 'PAID_SEAT_AT_COUNTER',
        messageLong: 'Opted to pay for seat at counter.',
      },
    },
    [TypesErrors.NO_ELIGIBLE_PASSENGERS]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: '',
        code: 'NO_ELIGIBLE_PASSENGERS',
        messageLong: 'No passengers eligible for kiosk checkin.',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: 'Please hand this card to an agent to receive attention at the counter',
        header4: '',
        code: 'NO_ELIGIBLE_PASSENGERS',
        messageLong: 'No passengers eligible for kiosk checkin.',
      },
    },
    [TypesErrors.ALL_SEATS_SELECTED]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: '',
        code: 'ALL_SEATS_SELECTED',
        messageLong: 'No está disponible la selección de asientos. Por favor, haga su check in',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: 'Please hand this card to an agent to receive attention at the counter',
        header4: '',
        code: 'ALL_SEATS_SELECTED',
        messageLong: 'No seat map available now. Please try to check in',
      },
    },
    [TypesErrors.REPRINT_BOARDING_PASSES_ERROR]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: '',
        code: 'REPRINT_BOARDING_PASSES_ERROR',
        messageLong:
          'The system can not reprint your boarding pass(es). Please proceed to a counter for help',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: 'Please hand this card to an agent to receive attention at the counter',
        header4: '',
        code: 'REPRINT_BOARDING_PASSES_ERROR',
        messageLong:
          'The system can not reprint your boarding pass(es). Please proceed to a counter for help',
      },
    },
    [TypesErrors.OVERBOOKING_CONFIRMATION]: {
      es_MX: {
        header1: '                    CONFIRMACIÓN DE VOLUNTARIADO                     ',
        header2: 'Gracias por aplicar como voluntario para viajar en un siguiente vuelo',
        header3: 'disponible a tu destino.',
        header4: 'Por favor, acércate con un asesor de servicio para que te de más     ',
        code: 'OVERBOOKING_VOLUNTEER_CONFIRMATION',
        messageLong: 'información y te indique el proceso a seguir con tu equipaje.    ',
      },
      en_US: {
        header1: '                       VOLUNTEER CONFIRMATION                        ',
        header2: '          Thank you for volunteering to take a later flight          ',
        header3: 'Please see an agent at the airport counter for further instructions  ',
        header4: 'regarding your volunteer and checked baggage.',
        code: 'OVERBOOKING_VOLUNTEER_CONFIRMATION',
        messageLong: '',
      },
    },
    [TypesErrors.WARNING_CONECCTION_BAGGAGE]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '          Usted tiene una maleta documentada, acuda a mostrador      ',
        header4: '',
        code: 'WARNING_CONECCTION_BAGGAGE',
        messageLong: 'Acuda al mostrador para mayor información',
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: '                 You have a checked bag, go to the counter           ',
        header4: '',
        code: 'WARNING_CONECCTION_BAGGAGE',
        messageLong: 'Go to the counter for more information',
      },
    },
    [TypesErrors.CHECKIN_OVS_ERROR]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN OVS                   ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: ' Tu vuelo presenta alta demanda y es necesario un proceso adicional  ',
        header4: ' para concluir la asignación de asientos, pasa a nuestros mostradores',
        code: 'CHECKIN_OVS_ERROR',
        messageLong: 'Acuda al mostrador para mayor información',
      },
      en_US: {
        header1: '                           INFORMATION CARD  OVS                     ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: '  An additional process is needed to conclude your seat assignment;  ',
        header4: '               please proceed to our counters                        ',
        code: 'CHECKIN_OVS_ERROR',
        messageLong: 'Go to the counter for more information',
      },
    },
    [TypesErrors.CHECKIN_ABOVE_AUTHORIZATION_ERROR]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                   ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: ' Tu vuelo presenta alta demanda y es necesario un proceso adicional  ',
        header4: '          para asignarte asiento. Pasa a nuestros mostradores.       ',
        code: 'CHECKIN_ABOVE_AUTHORIZATION_ERROR',
        messageLong: 'Acuda al mostrador para mayor información',
      },
      en_US: {
        header1: '                           INFORMATION CARD                      ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: 'Your flight is presenting high demand and an additional process is needed',
        header4: 'to conclude your seat assignment; please proceed to our counters     ',
        code: 'CHECKIN_ABOVE_AUTHORIZATION_ERROR',
        messageLong: 'Go to the counter for more information',
      },
    },
    [TypesErrors.DEFAULT_ERROR]: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                     ESTO NO ES UN PASE DE ABORDAR                   ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: ``,
        code: `${id}  ${msg}`,
        messageLong: `${id}  ${msg}`,
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: 'Please hand this card to an agent to receive attention at the counter',
        header4: ``,
        code: `${id}  ${msg}`,
        messageLong: `${id}  ${msg}`,
      },
    },
    [TypesErrors.SSBD_HTTP_503]: {
      es_MX: {
        header1: '      ERROR — Entregar a un agente en mostrador                      ',
        header2: '                    ESTO NO ES UN PASE DE ABORDAR                    ',
        header3: '    Código de error: 503 Service Temporarily Unavailable             ',
        header4: '',
        code: '',
        messageLong: '',
      },
      en_US: {
        header1: '          ERROR — Hand this card to a counter agent                  ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: '    Error code: 503 Service Temporarily Unavailable                  ',
        header4: '',
        code: '',
        messageLong: '',
      },
    },
    ['default']: {
      es_MX: {
        header1: '                        TARJETA DE INFORMACIÓN                       ',
        header2: '                     ESTO NO ES UN PASE DE ABORDAR                   ',
        header3: '    Por favor entregue esta tarjeta a un agente para ser atendido    ',
        header4: ``,
        code: `${id} | ${msg}`,
        messageLong: `${id} | ${msg}`,
      },
      en_US: {
        header1: '                           INFORMATION CARD                          ',
        header2: '                     THIS IS NOT A BOARDING PASS                     ',
        header3: 'Please hand this card to an agent to receive attention at the counter',
        header4: ``,
        code: `${id} | ${msg}`,
        messageLong: `${id} | ${msg}`,
      },
    },
  };
  return contentsErrors[id]?.[lang] ?? contentsErrors[defaultKey]?.[lang];
}

function getTextError(id) {
  const errors = {
    [TypesErrors.UNACCOMPANIED_MINOR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.unaccompanied_minor',
    },
    [TypesErrors.ALL_SEATS_SELECTED]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.all_seats_selected',
    },
    [TypesErrors.REPRINT_BOARDING_PASSES_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.reprint_boarding_passes',
    },
    [TypesErrors.GENERIC_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.generic_message',
    },
    [TypesErrors.PAYMENT_ERROR_RETRY]: {
      title: 'errorMsj.payment_header',
      text: 'errorMsj.payment_retry_message',
    },
    [TypesErrors.PAYMENT_ERROR_COUNTER]: {
      title: 'errorMsj.payment_header',
      text: 'errorMsj.payment_counter_message',
    },
    [TypesErrors.CREDIT_CARD_READ_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.reading_credit_card',
    },
    [TypesErrors.PASSPORT_SCAN_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.scan_passport',
    },
    [TypesErrors.RESERVATION_NOT_FOUND]: {
      title: 'errors.pnrLookup.reservationNoFound.title',
      text: 'errors.pnrLookup.reservationNoFound.text',
    },
    [TypesErrors.UNABLE_TO_REMOVE_ITEM]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.edit_cart.unable_to_remove',
    },
    [TypesErrors.CREDIT_CARD_DECLINED]: {
      title: 'errorMsj.credit_card_declined_header',
      text: 'errorMsj.credit_card_declined',
    },
    [TypesErrors.CREDIT_CARD_HOST_ERROR]: {
      title: 'errorMsj.credit_card_host_error_header',
      text: 'errorMsj.credit_card_host_error',
    },
    [TypesErrors.CHECKIN_ERROR]: {
      title: 'errorMsj.checkin_error_header',
      text: 'errorMsj.checkin_error_message',
    },
    [TypesErrors.CHECKIN_OVI_ERROR]: {
      title: 'errorMsj.checkin_error_header',
      text: 'errorMsj.checkin_ovi_error_message',
    },
    [TypesErrors.SCAN_PASSPORT_ERROR]: {
      title: 'errorMsj.Unable_to_read_passport',
      text: 'errorMsj.Unable_to_read_passport.description',
    },
    [TypesErrors.PINPAD_NOT_READY_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.pinpad_not_read_error.description',
    },
    [TypesErrors.TSA_VALIDATION]: {
      title: 'errorMsj.tsa_validation.header',
      text: 'errorMsj.tsa_validation.message',
    },
    [TypesErrors.STATIC_PROFILE_UPDATE]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.static_profile_update',
    },
    [TypesErrors.PROFILE_UPDATE]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.profile_update',
    },
    [TypesErrors.PINPAD_INIT_FAIL]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.pinpad-init-fail',
    },
    [TypesErrors.MANUALLY_ENTERED_CARD_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.pinpad_manually_entered_error',
    },
    [TypesErrors.EMAIL_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.email-error',
    },
    [TypesErrors.FAILED_TO_RESERVE_PREMIERE_UPGRADE]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.FAILED_TO_RESERVE_PREMIERE_UPGRADE',
    },
    [TypesErrors.OVERWEIGHT_BAG_CHECKIN_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.overweight_bag_checkin_error',
    },
    [TypesErrors.PAYMENT_NOT_SUPPORTED]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.PAYMENT_NOT_SUPPORTED',
    },
    [TypesErrors.CART_UPDATE_FAILED]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.CART_UPDATE_FAILED',
    },
    [TypesErrors.AGENT_SCAN_FAILED]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.AGENT_SCAN_FAILED',
    },
    [TypesErrors.FREQUENT_FLIER_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.FREQUENT_FLIER_ERROR',
    },
    [TypesErrors.BAG_TAG_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.BAG_TAG_ERROR',
    },
    [TypesErrors.OVER_BOOKING_UPDATE_ERROR]: {
      title: 'errorMsj.generic_header',
      text: 'errorMsj.OVER_BOOKING_UPDATE_ERROR',
    },
    [TypesErrors.LIMIT_BAGS]: {
      title: 'important',
      text: 'nextToCounter',
    },
    [TypesErrors.MAX_BAGGAGE_OVERWEIGHT_LIMIT]: {
      title: 'errorMsj.baggageOverweightLimit.title',
      text: 'errorMsj.baggageOverweightLimit.description',
    },
    [TypesErrors.SEAT_CHANGE_WARN]: {
      title: 'seatReview.errorMsj.seat_number_no_longer_available_1',
      text: 'seatReview.errorMsj.seat_number_no_longer_available_2',
    },
    [TypesErrors.SEATS_REMOVE_CART]: {
      title: 'seatReview.errorMsj.noSeatHeader',
      text: 'seatReview.errorMsj.noSeatMsg',
    },
    [TypesErrors.CHANGUE_FLIGHT_IROPS]: {
      title: 'errorMsj.msj.title',
      text: 'errorMsj.irops.err',
    },
    [TypesErrors.UPDATE_DOCUMENTS]: {
      title: 'errorMsj.msj.title',
      text: 'errorMsj.CART_UPDATE_FAILED',
    },
    [TypesErrors.ERROR_IN_REMARK]: {
      title: 'errorMsj.msj.title',
      text: 'iropsDeleteCHKModal.errorRemark',
    },
    [TypesErrors.PP_SCAN_FAIL]: {
      title: 'errorMsj.ppScanFail.title',
      text: 'errorMsj.ppScanFail.subtitle',
    },
    [TypesErrors.WERE_SORRY_GENERIC]: {
      title: 'errorMsj.msj.title',
      text: 'errorMsj.CART_UPDATE_FAILED',
    },
    [TypesErrors.NO_OVERWEIGHT]: {
      title: 'errorMsj.noOverWeight.title',
      text: 'errorMsj.noOverWeight.subtitle',
    },
    [TypesErrors.BAGS_CONNECTION_RESTRICTION]: {
      title: 'modalBagsConnectionRestriction.title',
      text: 'modalBagsConnectionRestriction.body',
    },
    [TypesErrors.BAGS_NO_KIOSK_AVAILABLE]: {
      title: 'modalBagsNoKioskAvailable.title',
      text: 'modalBagsNoKioskAvailable.body',
    },
    [TypesErrors.BAGS_INCLUDED_ONLY_AVAILABLE]: {
      title: 'modalBagsIncludedOnlyAvailable.title',
      text: 'modalBagsIncludedOnlyAvailable.body',
    },
    [TypesErrors.SSBD_HTTP_503]: {
      title: 'errorMsj.ssbdHttp503.title',
      text: 'errorMsj.ssbdHttp503.body',
    },
    [TypesErrors.FARE_FAMILY_EMPTY_ERROR]: {
      title: 'errorMsj.fareFamilyEmpty.title',
      text: 'errorMsj.fareFamilyEmpty.text',
    },
    [TypesErrors.NO_ITINERARY]: {
      title: 'errors.pnrLookup.noItinerary.title',
      text: 'errors.pnrLookup.noItinerary.text',
    },
    [TypesErrors.BAGS_STANDBY_COUNTER_REQUIRED]: {
      title: 'modalBagsStandbyCounter.title',
      text: 'modalBagsStandbyCounter.body',
    },
  };
  return errors[id] || '';
}

export { format, prepPectabDynamicContent, getTextError };
